import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { ResponsiveElement } from '../../library/elements/responsive-container.js';

import './d-organization-search.js';
import './d-organization-settings.js';
import './d-starttask-field.js';
import type { EventOccurrenceViewModel } from '@trinnvis/dabih-api-client';
import type { UiSettings } from '../../store/selectors/uiSettings';
import type { FeatureStates } from '../../store/selectors/featureStates';
import type { SettingsItem } from './settings-item';
import type { HeaderSearchView } from './header-search-view';
import type { HeaderSettingsView } from './header-settings-view';
import type { StartTask } from './start-task';
import type { TopSubmenu } from 'src/layout/parts/top-submenu';

/**
 * Meny som vises i toppen for en gitt organisasjon. Tilpasses skjermbredden.
 *
 *
 *
 */
@customElement('d-organization-header')
export class DOrganizationHeader extends ResponsiveElement {
  static readonly styles = css`
    :host {
      display: block;
      container-name: host;
      container-type: inline-size;
      padding-bottom: 12px;
    }

    .specialAlertHeader {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #edff6f;
      padding: 16px;
    }

    .specialAlertHeader strong {
      font-weight: 500;
    }

    .specialAlertHeader .closer {
      margin-left: 10px;
    }

    @media only screen and (max-width: 600px) {
      :host {
        padding-bottom: 0;
      }
    }

    .maxWidth600 :host {
      padding-bottom: 0;
    }

    .top-menu {
      display: flex;
      justify-content: flex-end;
      overflow: hidden;
      line-height: 120%;
    }

    .top-menu:first-child {
      flex: none;
    }

    .menu-item {
      box-sizing: border-box;
      height: 100%;
      flex: none;
      margin-left: 1px;
      padding: 16px 6px 0 34px;
      background-size: 20px auto;
      background-repeat: no-repeat;
      background-position: 8px 14px;
      font-size: 14px;
      color: white;
      white-space: nowrap;
      opacity: 0.7;
      overflow: hidden;
      transition:
        max-width 0.5s,
        padding 0.5s,
        opacity 0.5s;
      cursor: pointer;
    }

    .top-menu:last-child .menu-item {
      flex-shrink: 1;
    }

    .menu-item.disabled {
      cursor: default;
    }

    .menu-item:not(.disabled):hover,
    .menu-item[selected] {
      opacity: 1;
      transition: all 0s;
    }

    .menu-item.new {
      background-image: url(/images/topmenu/new.svg);
    }

    .menu-item.search {
      background-image: url(/images/topmenu/search.svg);
    }

    .menu-item.settings {
      background-image: url(/images/topmenu/settings.svg);
    }

    .menu-item.support {
      background-image: url(/images/topmenu/support.svg);
      background-position-x: 4px;
      padding-left: 26px;
    }

    .menu-item.tutorial {
      background-image: url(/images/topmenu/tutorial.svg);
      background-position-x: 4px;
      padding-left: 30px;
    }

    .menu-item.organization {
      background-image: url(/images/topmenu/organization.svg);
      padding-left: 36px;
    }

    .menu-item.ncc {
      background-image: url(/images/ncc-logo.svg);
      background-size: 48px;
      background-position-y: 10px;
      padding-left: 62px;
      position: relative;
    }

    .menu-item.user {
      background-image: url(/images/topmenu/user.svg);
      max-width: none;
      padding-right: 20px;
      flex-shrink: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .top-menu:not(.logout-expanded) .user {
      transition:
        max-width 0.5s,
        padding 0.5s,
        opacity 0.5s;
    }

    .menu-item > div {
      max-width: 0;
      opacity: 0;
      transition:
        max-width 0.5s,
        padding 0.5s,
        opacity 0.5s,
        visibility 0s 0.5s;
    }

    :host(.width845) .menu-item > div {
      max-width: none;
      opacity: 1;
      transition:
        max-width 0.5s,
        padding 0.5s,
        opacity 0.5s,
        visibility 0s;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .offlineHeader {
      padding: 10px;
      background-color: var(--alertColor);
      color: white;
      text-align: center;
    }

    .applicationHeader {
      display: flex;
      justify-content: space-between;
      position: relative;
      color: white;
      height: var(--applicationHeaderHeight);
      box-sizing: border-box;
    }

    .logo {
      flex: none;
      width: 97px;
      height: 25px;
      padding: 10px 18px 10px 20px;
      margin-bottom: -5px;
      background: url(/images/trinnvis-logo-blue-white.svg) 17px 10px no-repeat;
      background-size: 108px auto;
      text-indent: -10000px;
      font-weight: 500;
      font-size: 17px;
      line-height: 160%;
      color: var(--themeColorLighterTwo);
    }

    @media (hover: hover) {
      .logo:hover {
        color: white;
      }
    }

    :host([menu-covered]) .logo {
      background: none;
      text-indent: 0;
      cursor: pointer;
    }

    d-starttask-field {
      margin: 4px 0 0 0;
      max-width: 760px;
      overflow: hidden;
    }

    @container host (width > 600px) {
      d-starttask-field {
        margin: 4px 20px 0 20px;
        border-radius: 12px;
      }
    }

    .metaContentWrapper {
      margin: 0 20px 20px 20px;
      position: relative;
      box-sizing: border-box;
      background-color: hsla(196, 100%, 81%, 0.45);
      border-radius: 12px;
      transition: all 0.5s;
      max-width: var(--metaContentMaxWidth);
    }
  `;
  @property({ type: Object })
  organization: any = {};
  @property({ type: Number })
  organizationId = 0;
  @property({ type: String })
  currentOrganizationName = '';
  /**
   * Display message showing that application is currently offline
   */
  @property({ type: Boolean })
  offlineHeader = false;
  @property({ type: Boolean })
  specialAlertHeader = true;
  @property({ type: Object })
  user!: { username: string };
  @property({ type: Boolean })
  currentUserIsOwner = false;
  @property({ type: Boolean })
  currentUserHasMultipleOrganizations = false;
  @property({ type: Boolean })
  showLogout = false;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: String })
  displayedUserName = '';
  @property({ type: String })
  selectedPage = '';
  @property({ type: Boolean })
  collapsibleMenuOpen = false;
  @property({ type: Boolean })
  startTaskExpanded = false;
  @property({ type: Boolean })
  noStartTaskExpandAnimation = false;
  @property({ type: Boolean })
  helpViewerOpen = false;
  @property({ type: Boolean })
  tutorialActive = false;
  @property({ type: Boolean })
  tutorialViewerOpen = false;
  @property({ type: Object })
  headerSearchView?: HeaderSearchView = undefined;
  @property({ type: Object })
  headerSettingsView?: HeaderSettingsView = undefined;
  @property({ type: Boolean, attribute: 'menu-covered', reflect: true })
  menuCovered = false;
  /**
   * Is this single user version.
   */
  @property({ type: Boolean })
  singleUserVersion = false;
  /**
   * The list of start tasks
   */
  @property({ type: Number })
  userTotal = 0;
  @property({ type: Number })
  userExecuted = 0;
  @property({ type: Object })
  startTask?: StartTask;
  @property({ type: Object })
  startTaskEvent: EventOccurrenceViewModel | undefined = undefined;
  @property({ type: String })
  currentEmployeeUuid = '';
  @property({ type: String })
  selectedSettings = 'profile';
  @property({ type: Boolean })
  searchOpen = false;
  @property({ type: Boolean })
  settingsOpen = false;
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Object })
  uiSettings: UiSettings = {
    isSimplifiedUi: false,
  };
  private showLogoutTimeout;

  private get settingsMenuItems() {
    if (this.headerSettingsView !== undefined) {
      return this.headerSettingsView.items;
    }
    return [];
  }

  onResize() {
    const h = this.clientHeight;
    this.dispatchEvent(
      new CustomEvent<{ height: number }>('header-height-changed', {
        bubbles: true,
        composed: true,
        detail: { height: h },
      }),
    );
  }

  _uncoverPageMenu() {
    if (this.menuCovered) {
      this.dispatchEvent(new CustomEvent('uncover-menu', { bubbles: true, composed: true }));
    }
  }

  _navigateToAccounts() {
    this.dispatchEvent(new CustomEvent('navigate', { bubbles: true, composed: true, detail: { href: '/dashboard' } }));
  }

  private _isPerformanceReviewOnly(): boolean {
    return (
      this.featureStates.performanceReview &&
      !this.featureStates.core &&
      !this.featureStates.meetings &&
      !this.featureStates.hse &&
      !this.featureStates.staffing &&
      !this.featureStates.assets &&
      !this.featureStates.substances &&
      !this.featureStates.issues &&
      !this.featureStates.risk &&
      !this.featureStates.data &&
      !this.featureStates.quality &&
      !this.featureStates.healthcare &&
      !this.featureStates.collections
    );
  }

  private _shouldShowSearch(): boolean {
    // Hide search for users with only performanceReview access
    return !this._isPerformanceReviewOnly();
  }

  private _shouldShowStartTask(): boolean {
    // Hide start task (hurtigvalg) for users with only performanceReview access
    return !this._isPerformanceReviewOnly();
  }

  render() {
    return html`
      ${this.offlineHeader
        ? html` <div id="temporaryHeaders">
            <div class="offlineHeader">${t('organizationHeader.connectingToServer')}</div>
          </div>`
        : nothing}
      <div class="applicationHeader">
        <div class="top-menu">
          <span class="logo" @click=${this._uncoverPageMenu}>${t('organizationHeader.mainMenu')}</span>
          <div class="menu-item new" @click=${(e) => this.openSubmenu(e, 'new')}>
            <div>${t('organizationHeader.add')}</div>
          </div>
          ${this._shouldShowSearch()
            ? html` <div class="menu-item search" ?selected=${this.searchOpen} @click=${this.toggleSearch}>
                <div>${t('organizationHeader.search')}</div>
              </div>`
            : nothing}
          <div class="menu-item support" ?selected=${this.helpViewerOpen} @click=${this.fireToggleHelp}>
            <div>${t('organizationHeader.help')}</div>
          </div>
          ${this.tutorialActive || this.tutorialViewerOpen
            ? html`
                <div class="menu-item tutorial" ?selected=${this.tutorialViewerOpen} @click=${this.fireToggleTutorial}>
                  <div>${t('library.tutorials.coursesAndGuides')}</div>
                </div>
              `
            : nothing}
        </div>
        <div class="top-menu">
          ${this.featureStates?.ncc
            ? html`
                <div class="menu-item ncc" @click=${this._navigateToAccounts}>
                  <div>${this.currentOrganizationName}</div>
                </div>
              `
            : html`
                ${this.currentUserIsOwner || this.currentUserHasMultipleOrganizations
                  ? html`
                      <div class="menu-item organization" @click=${(e) => this.openSubmenu(e, 'organization')}>
                        <div>${this.currentOrganizationName}</div>
                      </div>
                    `
                  : html`
                      <div class="menu-item organization disabled">
                        <div>${this.currentOrganizationName}</div>
                      </div>
                    `}
              `}
          <div class="menu-item user" @click=${(e) => this.openSubmenu(e, 'user')}>
            <div>${this.displayedUserName}</div>
          </div>
        </div>
      </div>
      ${this.headerSearchView !== undefined
        ? html` <d-organization-search
            id="orgSearch"
            @start-task-selected=${this.toggleSearch}
            .query=${this.headerSearchView.query}
            .results=${this.headerSearchView.results}
            .offlineSearch=${this.headerSearchView.offlineSearch}
            @close-search=${() => this.toggleSearch()}
            @search-query-changed=${() => this.onResize()}
            @search-updated=${() => this.onResize()}
            ?open=${this.searchOpen}
          >
          </d-organization-search>`
        : nothing}
      <d-organization-settings
        organization="${this.organization}"
        .organizationId=${this.organizationId}
        selected-page="${this.selectedPage}"
        .user=${this.user}
        .settingsItem=${this.computeSettingsItem()}
        .settingsMenuItems=${this.settingsMenuItems}
        .writeAccess=${this.writeAccess}
        .featureStates=${this.featureStates}
        @close-settings=${this.toggleSettings}
        ?open=${this.settingsOpen}
        .uiSettings=${this.uiSettings}
      >
      </d-organization-settings>
      ${!this.searchOpen && !this.settingsOpen && this.startTask !== undefined && this._shouldShowStartTask()
        ? html` <d-starttask-field
            ?open=${this.startTaskExpanded}
            .singleUserVersion=${this.singleUserVersion}
            ?noExpandAnimation=${this.noStartTaskExpandAnimation}
            .userTotal=${this.userTotal}
            .userExecuted=${this.userExecuted}
            ?showNotification=${false}
            .startTask=${this.startTask}
            .startTaskEvent=${this.startTaskEvent}
            .currentEmployeeUuid=${this.currentEmployeeUuid}
          ></d-starttask-field>`
        : nothing}
    `;
  }

  toggleSearch() {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      this.settingsOpen = false;
    }
    this.dispatchEvent(
      new CustomEvent<{ value: boolean }>('toggle-search', {
        bubbles: true,
        composed: true,
        detail: { value: this.searchOpen },
      }),
    );
  }

  protected willUpdate() {
    this.checkForShowUserSettings();
  }

  private openSubmenu(e, submenu: TopSubmenu) {
    const menuItem: HTMLDivElement = e.target.closest('.menu-item');
    const position = Math.round(menuItem.getBoundingClientRect().left);
    this.searchOpen = false;
    this.dispatchEvent(
      new CustomEvent<{ submenu: TopSubmenu; position: number }>('open-top-submenu', {
        bubbles: true,
        composed: true,
        detail: { submenu, position },
      }),
    );
  }

  private toggleSettings() {
    this.settingsOpen = !this.settingsOpen;
    if (this.settingsOpen) {
      this.searchOpen = false;
    }
    this.dispatchEvent(
      new CustomEvent<{ value: boolean }>('toggle-settings', {
        bubbles: true,
        composed: true,
        detail: { value: this.settingsOpen },
      }),
    );
  }

  private fireToggleHelp() {
    if (this.helpViewerOpen) {
      this.dispatchEvent(new CustomEvent<{ value: boolean }>('close-viewer', { bubbles: true, composed: true }));
      setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent<{ value: boolean }>('toggle-help', {
            bubbles: true,
            composed: true,
            detail: { value: false },
          }),
        );
      }, 500);
    } else {
      this.dispatchEvent(
        new CustomEvent<{ value: boolean }>('toggle-help', {
          bubbles: true,
          composed: true,
          detail: { value: true },
        }),
      );
    }
  }

  private fireToggleTutorial() {
    if (this.tutorialViewerOpen) {
      this.dispatchEvent(new CustomEvent<{ value: boolean }>('close-viewer', { bubbles: true, composed: true }));
      setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent<{ value: boolean }>('toggle-tutorial', {
            bubbles: true,
            composed: true,
            detail: { value: false },
          }),
        );
      }, 500);
    } else {
      this.dispatchEvent(
        new CustomEvent<{ value: boolean }>('toggle-tutorial', {
          bubbles: true,
          composed: true,
          detail: { value: true },
        }),
      );
    }
  }

  private computeSettingsItem(): SettingsItem {
    if (this.headerSettingsView === undefined) {
      throw new Error('headerSettingsView is not defined');
    }
    const settingsItem = this.headerSettingsView.items.find((item) => item.type === this.selectedSettings);
    if (settingsItem === undefined) {
      this.selectedSettings = 'profile';
      return (
        this.headerSettingsView.items.find((item) => item.type === this.selectedSettings) ??
        this.headerSettingsView.items[0]
      );
    }
    return settingsItem;
  }

  private checkForShowUserSettings() {
    const query = location.search.slice(1);
    const params = new URLSearchParams(query);
    if (params.has('showUserSettings')) {
      this.selectedSettings = 'profile';
      if (!this.settingsOpen) {
        this.toggleSettings();
      }
      history.replaceState({}, '', location.protocol + '//' + location.host + location.pathname);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-header': DOrganizationHeader;
  }
}
